import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  ListSubheader, Divider, Box
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Business as CompanyIcon,
  Groups as TeamIcon,
  CreditCard as BillingIcon,
  ElectricBolt as TariffIcon,
  Construction as EquipmentIcon,
  ShowChart as LoadProfileIcon,
  AccountBalance as BudgetIcon,
  Domain as AllCompaniesIcon,
  People as AllUsersIcon,
  Memory as SolverIcon,
  AttachMoney as CapExIcon,
  Bolt as DerChargesIcon,
  BugReport as BugIcon,
  Visibility as ActivityIcon,
  ElectricCar as EVIcon
} from '@mui/icons-material';
import { vortxColors, useThemeMode } from '@vortx/shared-ui';

const drawerWidth = 240;

const AdminNav = ({ isSuperAdmin }) => {
  const { isRetro } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();

  const accountManagementItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
    { text: 'Activity Monitor', icon: <ActivityIcon />, path: '/admin/activity' },
    { text: 'Company Settings', icon: <CompanyIcon />, path: '/admin/company' },
    { text: 'Team & Seats', icon: <TeamIcon />, path: '/admin/team' },
    { text: 'Billing', icon: <BillingIcon />, path: '/admin/billing' },
  ];

  const productConfigItems = [
    { text: 'Tariff Management', icon: <TariffIcon />, path: '/admin/tariffs' },
    { text: 'CAPEX & OPEX', icon: <CapExIcon />, path: '/admin/capex' },
    { text: 'Equipment Library', icon: <EquipmentIcon />, path: '/admin/equipment' },
    { text: 'Load Profiles', icon: <LoadProfileIcon />, path: '/admin/load-profiles' },
    { text: 'Budget Templates', icon: <BudgetIcon />, path: '/admin/budgets' },
    { text: 'EV Vehicles', icon: <EVIcon />, path: '/admin/ev-vehicles' },
  ];

  const platformManagementItems = [
    { text: 'All Companies', icon: <AllCompaniesIcon />, path: '/admin/companies' },
    { text: 'All Users', icon: <AllUsersIcon />, path: '/admin/users' },
    { text: 'Solver Monitor', icon: <SolverIcon />, path: '/admin/solver' },
    { text: 'CA DER Charges', icon: <DerChargesIcon />, path: '/admin/der-charges' },
    { text: 'Bug Reports', icon: <BugIcon />, path: '/admin/bugs' },
  ];

  const handleNavClick = (e, path) => {
    // Allow ctrl+click, cmd+click, or middle-click to open in new tab
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return; // Let browser handle it
    }
    e.preventDefault();
    navigate(path);
  };

  const renderNavSection = (title, items) => (
    <>
      <ListSubheader
        component="div"
        sx={{
          lineHeight: '32px',
          fontSize: '0.7rem',
          fontWeight: 600,
          letterSpacing: '0.05em',
          color: vortxColors.stone,
          bgcolor: 'transparent',
          mt: 1
        }}
      >
        {title}
      </ListSubheader>
      {items.map((item) => (
        <ListItem key={item.text} disablePadding>
          <ListItemButton
            component="a"
            href={item.path}
            dense
            selected={location.pathname === item.path}
            onClick={(e) => handleNavClick(e, item.path)}
            sx={{
              mx: 1,
              borderRadius: 1,
              textDecoration: 'none',
              '&.Mui-selected': {
                bgcolor: alpha(vortxColors.gold, 0.15),
                '&:hover': {
                  bgcolor: alpha(vortxColors.gold, 0.2)
                }
              },
              '&:hover': {
                bgcolor: alpha(vortxColors.gold, 0.08)
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem' }} />
          </ListItemButton>
        </ListItem>
      ))}
    </>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: 0,
          height: '100vh',
          bgcolor: isRetro ? '#1a1a1a' : '#f5f5f5',
          borderRight: 'none',
          borderLeft: `2px solid ${vortxColors.teal}`,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Spacer for header */}
      <Box sx={{ height: 64, flexShrink: 0 }} />
      <List dense disablePadding sx={{
        pt: 2,
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: isRetro ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(95, 212, 196, 0.4)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(95, 212, 196, 0.6)',
          },
        },
      }}>
        {renderNavSection('ACCOUNT MANAGEMENT', accountManagementItems)}
        <Divider sx={{ my: 1.5, mx: 2, borderColor: alpha(vortxColors.stone, 0.2) }} />
        {renderNavSection('PRODUCT CONFIGURATION', productConfigItems)}
        {isSuperAdmin && (
          <>
            <Divider sx={{ my: 1.5, mx: 2, borderColor: alpha(vortxColors.stone, 0.2) }} />
            {renderNavSection('PLATFORM MANAGEMENT', platformManagementItems)}
          </>
        )}
      </List>
    </Drawer>
  );
};

export default AdminNav;
